import React, { useState, useEffect, useRef, useCallback } from "react";

const SimpleNameAutocomplete = ({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  onNameSelect,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [allNames, setAllNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch names from API
  useEffect(() => {
    const fetchNames = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          "https://names-storage.t9e2i3kal.workers.dev/list"
        );
        if (response.ok) {
          const data = await response.json();
          console.log("Fetched names:", data);
          setAllNames(data || []);
        }
      } catch (error) {
        console.error("Failed to fetch names:", error);
        setAllNames([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNames();
  }, []);

  // Filter suggestions based on input
  const filterSuggestions = useCallback(
    (inputValue) => {
      if (!inputValue || inputValue.trim().length < 2) {
        setSuggestions([]);
        return;
      }

      const filtered = allNames
        .filter((name) => name.toUpperCase().includes(inputValue.toUpperCase()))
        .map((name) => name.toUpperCase())
        .slice(0, 5);

      setSuggestions(filtered);
      setSelectedIndex(-1);
    },
    [allNames]
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value.toUpperCase();
    onChange(newValue);
    filterSuggestions(newValue);
  };

  const handleSuggestionClick = (suggestion) => {
    const parts = suggestion.trim().split(/\s+/);
    let firstName = "";
    let lastName = "";

    if (parts.length >= 2) {
      // Line 1 gets last name (split on space [1])
      // Line 2 gets first name (split on space [0])
      firstName = parts[0].toUpperCase() || "";
      lastName = parts[1].toUpperCase() || "";
    } else if (parts.length === 1) {
      // Only one name, treat as first name
      firstName = parts[0].toUpperCase() || "";
      lastName = "";
    }

    if (onNameSelect) {
      // Enhanced mode: provide name parts with correct mapping
      onNameSelect({
        firstName, // This goes to Line 2
        lastName, // This goes to Line 1
        fullName: suggestion.toUpperCase(),
      });
    } else {
      // Original behavior for backward compatibility
      onChange(suggestion.toUpperCase());
    }

    setSuggestions([]);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > -1 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setSuggestions([]);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    console.log("Input focused, current value:", value);
    if (value && value.trim().length >= 2) {
      filterSuggestions(value);
    }
  };

  // Watch the value prop directly for changes from on-screen keyboard
  useEffect(() => {
    const newValue = value;
    console.log("Value changed via on-screen keyboard:", newValue);
    if (newValue && newValue.length >= 2) {
      const filtered = allNames
        .filter((name) => name.toUpperCase().includes(newValue.toUpperCase()))
        .map((name) => name.toUpperCase())
        .slice(0, 5);
      console.log("Filtered suggestions from value watcher:", filtered);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [value, allNames]);

  return (
    <div className="dogtag-autocomplete-wrapper" ref={wrapperRef}>
      <div className="dogtag-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={`dogtag-input ${className || ""}`}
          disabled={disabled || isLoading}
        />
      </div>

      {/* Always visible suggestions list with site theme */}
      {suggestions.length > 0 && (
        <div style={{
          background: '#111',
          border: '2px solid #e6c618',
          borderRadius: '4px',
          marginTop: '5px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
              style={{
                padding: '12px 15px',
                cursor: 'pointer',
                backgroundColor: selectedIndex === index ? '#e6c618' : 'transparent',
                color: selectedIndex === index ? '#000' : '#fff',
                fontFamily: 'Courier New, monospace',
                fontSize: '14px',
                fontWeight: '500',
                borderBottom: index < suggestions.length - 1 ? '1px solid #333' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SimpleNameAutocomplete;
